import React, { useState } from 'react'
import { Box, Tab, Tabs, Typography, type SxProps, type Theme } from '@mui/material'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import { muahangKiotLike } from '../../layout/muahangKiotTokens'
import { baseModalDefaultFooterSx } from '../base'
import {
  kiotExpandDetailEmptyHintSx,
  kiotExpandDetailFooterSx,
  kiotExpandDetailGeneralPanelSx,
  kiotExpandDetailHeaderActionsSx,
  kiotExpandDetailHeaderBarSx,
  kiotExpandDetailInfoGridCompactSx,
  kiotExpandDetailInfoGridSx,
  kiotExpandDetailMetaRowSx,
  kiotExpandDetailStatCardSx,
  kiotExpandDetailTabPanelSx,
  kiotExpandDetailTabsSx,
} from './kiotListExpandTokens'

export type KiotListExpandTabItem = {
  value: string
  label: React.ReactNode
  panel: React.ReactNode
  disabled?: boolean
}

export type KiotListExpandDetailTabsProps = {
  tabs: KiotListExpandTabItem[]
  defaultTab?: string
  /** Controlled tab value — ưu tiên hơn state nội bộ. */
  value?: string
  onChange?: (value: string) => void
  /**
   * Nút hành động — hiện cùng hàng title tab (căn phải) và dưới panel.
   * Truyền hàm để mỗi slot mount node riêng (tránh reuse 1 React element).
   */
  actions?: React.ReactNode | (() => React.ReactNode)
  /** Footer tùy chỉnh — nếu không có `actions` thì dùng như trước. */
  footer?: React.ReactNode
  /** Hiện thanh tab kể cả khi chỉ có 1 tab (vd. YCSC chỉ «Thông tin»). */
  alwaysShowTabs?: boolean
  /** Mặc định true (banhang). Set false khi chỉ cần actions trên header (vd. Export). */
  duplicateActionsInFooter?: boolean
}

const resolveActions = (actions?: React.ReactNode | (() => React.ReactNode)) =>
  typeof actions === 'function' ? actions() : actions

export const KiotListExpandDetailTabs: React.FC<KiotListExpandDetailTabsProps> = ({
  tabs,
  defaultTab,
  value,
  onChange,
  actions,
  footer,
  alwaysShowTabs = false,
  duplicateActionsInFooter = true,
}) => {
  const firstTab = tabs[0]?.value ?? ''
  const [internalTab, setInternalTab] = useState(defaultTab ?? firstTab)
  const activeTab = value ?? internalTab
  const safeTab = tabs.some((t) => t.value === activeTab) ? activeTab : firstTab
  const activePanel = tabs.find((t) => t.value === safeTab)?.panel
  const showTabBar = tabs.length > 1 || (alwaysShowTabs && tabs.length > 0)
  const hasActions = actions != null
  const showHeaderBar = showTabBar || hasActions

  const setTab = (next: string) => {
    if (value == null) setInternalTab(next)
    onChange?.(next)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        flex: '1 1 auto',
        overflow: 'hidden',
      }}
    >
      {showHeaderBar ? (
        <Box sx={kiotExpandDetailHeaderBarSx}>
          {showTabBar ? (
            <Tabs
              value={safeTab}
              onChange={(_, next: string) => setTab(next)}
              variant="scrollable"
              scrollButtons={false}
              sx={kiotExpandDetailTabsSx}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  disabled={tab.disabled}
                  disableRipple
                />
              ))}
            </Tabs>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          {hasActions ? <Box sx={kiotExpandDetailHeaderActionsSx}>{resolveActions(actions)}</Box> : null}
        </Box>
      ) : null}
      <Box sx={kiotExpandDetailTabPanelSx} data-kiot-expand-body-scroll>
        {activePanel}
      </Box>
      {hasActions && duplicateActionsInFooter ? (
        <KiotListExpandDetailFooter
          sx={{ justifyContent: 'flex-end', gap: 0.75, flexWrap: 'wrap', flexShrink: 0 }}
          left={null}
          right={resolveActions(actions)}
        />
      ) : (
        footer
      )}
    </Box>
  )
}

export type KiotListExpandInfoFieldLayout = 'stacked' | 'inline'

export type KiotListExpandInfoFieldItem = {
  label: string
  value: React.ReactNode
  span?: 1 | 2 | 3 | 4
  /** Cho phép xuống dòng (địa chỉ, ghi chú). Mặc định ellipsis 1 dòng. */
  multiline?: boolean
  /** Ẩn field khi false (tiện declarative). */
  hidden?: boolean
  /** `inline`: label và value cùng dòng — tiết kiệm chiều cao. */
  layout?: KiotListExpandInfoFieldLayout
}

export type KiotListExpandInfoFieldProps = KiotListExpandInfoFieldItem

export const KiotListExpandInfoField: React.FC<KiotListExpandInfoFieldProps> = ({
  label,
  value,
  span = 1,
  multiline = false,
  layout = 'stacked',
}) => {
  const fieldFontSize = `${muahangKiotLike.kiotFontSizePx}px`
  const valueSx = {
    fontSize: fieldFontSize,
    fontWeight: layout === 'inline' ? 600 : 500,
    color: muahangKiotLike.kiotTextPrimary,
    lineHeight: muahangKiotLike.kiotLineHeight,
    minWidth: 0,
    fontFamily: muahangKiotLike.kiotFontStack,
    ...(multiline
      ? { wordBreak: 'break-word', whiteSpace: 'pre-wrap' }
      : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
  } as const

  if (layout === 'inline' && !multiline) {
    return (
      <Box
        sx={{
          minWidth: 0,
          display: 'flex',
          alignItems: 'baseline',
          gap: 0.5,
          gridColumn: span > 1 ? { xs: '1 / -1', lg: `span ${span}` } : undefined,
        }}
      >
        <Typography
          component="span"
          sx={{
            flexShrink: 0,
            fontSize: fieldFontSize,
            color: muahangKiotLike.kiotTextMuted,
            lineHeight: muahangKiotLike.kiotLineHeight,
            fontWeight: 500,
            fontFamily: muahangKiotLike.kiotFontStack,
          }}
        >
          {label}:
        </Typography>
        <Typography component="span" sx={valueSx}>
          {value}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minWidth: 0,
        gridColumn: span > 1 ? { xs: '1 / -1', lg: `span ${span}` } : undefined,
      }}
    >
      <Typography
        component="div"
        sx={{
          fontSize: '0.8125rem',
          color: muahangKiotLike.kiotTextMuted,
          lineHeight: muahangKiotLike.kiotLineHeight,
          mb: 0.5,
          fontWeight: 500,
          fontFamily: muahangKiotLike.kiotFontStack,
        }}
      >
        {label}
      </Typography>
      <Typography component="div" sx={valueSx}>
        {value}
      </Typography>
    </Box>
  )
}

export type KiotListExpandInfoGridProps = {
  children: React.ReactNode
}

export const KiotListExpandInfoGrid: React.FC<KiotListExpandInfoGridProps> = ({ children }) => (
  <Box sx={kiotExpandDetailInfoGridSx}>{children}</Box>
)

export type KiotListExpandInfoAvatarProps = {
  src?: string | null
  size?: number
  alt?: string
}

/** Avatar / thumbnail chuẩn cho tab Thông tin. */
export const KiotListExpandInfoAvatar: React.FC<KiotListExpandInfoAvatarProps> = ({
  src,
  size = 88,
  alt = '',
}) => {
  const url = src != null && String(src).trim() !== '' ? String(src).trim() : ''
  if (url) {
    return (
      <Box
        component="img"
        src={url}
        alt={alt}
        sx={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: '4px',
          border: '1px solid',
          borderColor: 'divider',
          display: 'block',
        }}
      />
    )
  }
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '4px',
        bgcolor: '#f5f5f5',
        border: '1px solid',
        borderColor: 'divider',
      }}
    />
  )
}

export type KiotListExpandInfoTabDensity = 'default' | 'compact'

export type KiotListExpandInfoTabProps = {
  /** Ảnh / avatar bên trái (optional). */
  media?: React.ReactNode
  /** Tiêu đề chính — thường là tên bản ghi. */
  title?: React.ReactNode
  /** Dòng phụ dưới title (mã, nhóm…). */
  subtitle?: React.ReactNode
  /** Chip / badge trạng thái dưới subtitle. */
  meta?: React.ReactNode
  /** `compact`: header 1 dòng + field inline — tiết kiệm chiều cao panel expand. */
  density?: KiotListExpandInfoTabDensity
  /** Layout mặc định cho fields (có thể override từng field). */
  fieldLayout?: KiotListExpandInfoFieldLayout
  /** Fields declarative — dùng chung nhiều màn. */
  fields?: KiotListExpandInfoFieldItem[]
  /** Nội dung bổ sung dưới grid (hoặc thay thế fields). */
  children?: React.ReactNode
  /** Link / action phụ dưới fields. */
  footerLinks?: React.ReactNode
}

/**
 * Base tab "Thông tin" cho panel mở rộng danh sách.
 * Layout: [media] + title/subtitle/meta + grid fields — dùng lại KH, NCC, SP…
 */
export const KiotListExpandInfoTab: React.FC<KiotListExpandInfoTabProps> = ({
  media,
  title,
  subtitle,
  meta,
  density = 'default',
  fieldLayout: fieldLayoutProp,
  fields,
  children,
  footerLinks,
}) => {
  const isCompact = density === 'compact'
  const fieldLayout = fieldLayoutProp ?? (isCompact ? 'inline' : 'stacked')
  const visibleFields = (fields ?? []).filter((f) => !f.hidden)
  const hasHeader = title != null || subtitle != null || meta != null

  return (
    <Box
      sx={{
        display: 'flex',
        gap: isCompact ? 1.25 : 2,
        px: isCompact ? 2 : 2.5,
        py: isCompact ? 1.25 : 2,
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        bgcolor: '#fff',
        alignItems: 'flex-start',
      }}
    >
      {media ? <Box sx={{ flexShrink: 0 }}>{media}</Box> : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {hasHeader ? (
          <Box sx={{ mb: visibleFields.length || children || footerLinks ? (isCompact ? 0.75 : 1.5) : 0 }}>
            {isCompact ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  minWidth: 0,
                }}
              >
                {title != null ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: muahangKiotLike.kiotTextPrimary,
                      lineHeight: muahangKiotLike.kiotLineHeight,
                      fontFamily: muahangKiotLike.kiotFontStack,
                      flexShrink: 0,
                    }}
                  >
                    {title}
                  </Typography>
                ) : null}
                {subtitle != null ? (
                  <Typography
                    component="span"
                    sx={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      fontSize: `${muahangKiotLike.kiotFontSizePx}px`,
                      fontWeight: 500,
                      color: muahangKiotLike.kiotTextPrimary,
                      lineHeight: muahangKiotLike.kiotLineHeight,
                      fontFamily: muahangKiotLike.kiotFontStack,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
                {meta != null ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 0.5,
                      ml: 'auto',
                      flexShrink: 0,
                    }}
                  >
                    {meta}
                  </Box>
                ) : null}
              </Box>
            ) : (
              <>
                {title != null ? (
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: muahangKiotLike.kiotTextPrimary,
                      lineHeight: 1.35,
                      mb: subtitle != null || meta != null ? 0.35 : 0,
                    }}
                  >
                    {title}
                  </Typography>
                ) : null}
                {subtitle != null ? (
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '0.8125rem',
                      color: muahangKiotLike.kiotTextMuted,
                      lineHeight: 1.4,
                      mb: meta != null ? 1 : 0,
                    }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
                {meta != null ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>{meta}</Box>
                ) : null}
              </>
            )}
          </Box>
        ) : null}
        {visibleFields.length > 0 ? (
          <Box sx={isCompact ? kiotExpandDetailInfoGridCompactSx : kiotExpandDetailInfoGridSx}>
            {visibleFields.map((field, idx) => (
              <KiotListExpandInfoField
                key={`${field.label}-${idx}`}
                label={field.label}
                value={field.value}
                span={field.span}
                multiline={field.multiline}
                layout={field.layout ?? fieldLayout}
              />
            ))}
          </Box>
        ) : null}
        {children}
        {footerLinks != null ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.25 }}>{footerLinks}</Box>
        ) : null}
      </Box>
    </Box>
  )
}

export type KiotListExpandGeneralPanelProps = {
  meta?: React.ReactNode
  children: React.ReactNode
}

/** @deprecated Ưu tiên `KiotListExpandInfoTab` — giữ để tương thích tab mô tả / panel cũ. */
export const KiotListExpandGeneralPanel: React.FC<KiotListExpandGeneralPanelProps> = ({ meta, children }) => (
  <Box sx={kiotExpandDetailGeneralPanelSx}>
    {meta ? <Box sx={kiotExpandDetailMetaRowSx}>{meta}</Box> : null}
    {children}
  </Box>
)

export type KiotListExpandDebtStat = {
  label: string
  value: React.ReactNode
  highlight?: boolean
}

export type KiotListExpandDebtTabPanelProps = {
  stats: KiotListExpandDebtStat[]
  hint: React.ReactNode
}

export const KiotListExpandDebtTabPanel: React.FC<KiotListExpandDebtTabPanelProps> = ({ stats, hint }) => (
  <Box sx={{ px: 2.5, py: 2 }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(168px, 220px))' },
        gap: 1.5,
        mb: 2,
      }}
    >
      {stats.map((stat) => (
        <Box
          key={stat.label}
          sx={
            [
              kiotExpandDetailStatCardSx,
              stat.highlight
                ? {
                    borderColor: 'rgba(0, 112, 244, 0.22)',
                    bgcolor: '#f5f9ff',
                  }
                : {},
            ] as SxProps<Theme>
          }
        >
          <Typography sx={{ fontSize: '0.75rem', color: '#666', mb: 0.5, lineHeight: 1.35, fontWeight: 500 }}>
            {stat.label}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: stat.highlight ? muahangKiotLike.linkBlue : muahangKiotLike.kiotTextPrimary,
              lineHeight: 1.2,
            }}
          >
            {stat.value}
          </Typography>
        </Box>
      ))}
    </Box>
    <Box sx={kiotExpandDetailEmptyHintSx}>
      <ReceiptLongOutlinedIcon sx={{ fontSize: 28, color: 'rgba(0,0,0,0.22)', mb: 0.75 }} />
      <Typography sx={{ fontSize: '0.8125rem', color: '#666', lineHeight: 1.5, maxWidth: 360 }}>{hint}</Typography>
    </Box>
  </Box>
)

export type KiotListExpandDetailFooterProps = {
  left?: React.ReactNode
  right?: React.ReactNode
  sx?: SxProps<Theme>
}

export const KiotListExpandDetailFooter: React.FC<KiotListExpandDetailFooterProps> = ({ left, right, sx }) => (
  <Box
    sx={
      [
        baseModalDefaultFooterSx,
        kiotExpandDetailFooterSx,
        { justifyContent: 'space-between' },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ] as SxProps<Theme>
    }
  >
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{left}</Box>
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{right}</Box>
  </Box>
)
